import { SkyButton, SkySwitch, SkyText, SkyTextarea } from "@skypointcloud/skypoint-ui";
import {
  MAX_RULE_EMAILS,
  SUCCESS_RULE_HELP,
  WARNING_RULE_HELP,
  ruleOutcomeLabel,
} from "@shared/types/notifications";
import { isExternalAddress } from "@shared/types/notificationDomains";
import { useHeldAddresses, useRecipientPolicy } from "@/lib/notifications/RecipientPolicyContext";
import {
  COPY_RULE_HELP,
  COPY_RULE_LABEL,
  cellKey,
  emailLines,
  emailsProblem,
  externalLine,
  type RuleDraft,
} from "@/lib/notifications/rulesDraft";

/** The rule's address box and Teams switch, or their read-only text. */
function RuleRecipients({
  rule,
  id,
  readOnly,
  emailsMasked,
  addresses,
  problem,
  onChange,
}: {
  rule: RuleDraft;
  id: string;
  readOnly: boolean;
  emailsMasked: boolean;
  addresses: string[];
  problem: string | null;
  onChange: (next: RuleDraft) => void;
}) {
  const policy = useRecipientPolicy();
  const external = readOnly ? null : externalLine(addresses, policy);
  return (
    <div className="flex flex-col gap-3">
      {readOnly ? (
        <SkyText size={200} block testId={`rule-emails-${id}`}>
          Email:{" "}
          {addresses.length > 0
            ? addresses
                .map((address) =>
                  isExternalAddress(address, policy) ? `${address} (External)` : address,
                )
                .join(", ")
            : "no address"}
          {emailsMasked && addresses.length > 0 ? " (masked)" : ""}
        </SkyText>
      ) : (
        <>
          <SkyTextarea
            label="Email"
            value={rule.emailsText}
            rows={Math.min(Math.max(addresses.length + 1, 2), 6)}
            hint={`One address per line, up to ${MAX_RULE_EMAILS}`}
            validationState={problem ? "error" : "none"}
            validationMessage={problem ?? undefined}
            testId={`rule-emails-${id}`}
            onChange={(event) => onChange({ ...rule, emailsText: event.target.value })}
          />
          {/* #1111: External addresses are marked whatever the tenant's setting (owner, 10/15). */}
          {external ? (
            <SkyText size={200} color="muted" block testId={`rule-external-${id}`}>
              {external}
            </SkyText>
          ) : null}
        </>
      )}

      {readOnly ? (
        <SkyText size={200} block testId={`rule-teams-${id}`}>
          Post to this instance&apos;s Teams channel: {rule.teams ? "On" : "Off"}
        </SkyText>
      ) : (
        <SkySwitch
          label="Post to this instance's Teams channel"
          checked={rule.teams}
          testId={`rule-teams-${id}`}
          onCheckedChange={(checked) => onChange({ ...rule, teams: checked })}
        />
      )}
    </div>
  );
}

/** The copy button, its sentence, and the persistent line that reports the copy. */
function CopyRuleRow({
  id,
  helpId,
  copyHelpShown,
  copyStatus,
  onCopy,
}: {
  id: string;
  helpId: string;
  copyHelpShown: boolean;
  copyStatus: string | null;
  onCopy: () => void;
}) {
  return (
    <div className="mt-3 flex flex-col gap-2">
      {/* Round 6 walk W24 asked for less repetition; the owner's round 3 note (always
        shown, not only after a click or a hover) stands for the button. Its sentence is
        said once on screen (walk 2026-10-02, D14), under the first rule that is on. */}
      <div data-copy-row className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <SkyButton
          appearance="secondary"
          size="small"
          testId={`copy-rule-${id}`}
          aria-describedby={helpId}
          onClick={onCopy}
        >
          {COPY_RULE_LABEL}
        </SkyButton>
        {/* Round 3 (owner, 2026-09-30): what the button does, beside it, shown with it
          and not only after a click or a hover. */}
        <SkyText
          as="p"
          size={200}
          color="muted"
          block
          id={helpId}
          testId={helpId}
          className={copyHelpShown ? "min-w-[16rem] flex-1" : "sr-only"}
        >
          {COPY_RULE_HELP}
        </SkyText>
      </div>
      {/* A persistent line under the button (ux-s10 §3 rule 3): the result replaces nothing,
        so it is neither a page notice nor a toast. */}
      <span role="status" data-testid={`copy-rule-status-${id}`}>
        {copyStatus ? (
          <SkyText size={200} color="muted">
            {copyStatus}
          </SkyText>
        ) : null}
      </span>
    </div>
  );
}

/**
 * One rule: a (What, When) cell of the Notifications tab (contract-notif R10, R14).
 *
 * A nested `fieldset` whose legend is the "When" words ("When a run fails"), inside the subject's
 * fieldset ("What: Imports"), so a screen reader announces both. The rule's own switch sits in
 * the legend, labelled with the same words; an off rule shows only that line (contract §4.2),
 * so "Use these recipients for every rule", and for the success rule the sentence saying a
 * recovery is a success message, appear once a rule is on. Round 3: no box of its own (its
 * subject's section is the one container), and no L1 switch: the L1 team hears every failure.
 * Round 6 (walk W24): where a pointer can hover, the copy button and its sentence show only on
 * the rule the pointer is over or focus is in, so a list of rules that are on is not a column of
 * copy buttons; a touch screen always shows them.
 *
 * Read-only (a caller without `instance.settings.write`): every value is text, with no switch,
 * no address box and no copy button.
 */
export function NotificationRuleFieldset({
  rule,
  readOnly,
  emailsMasked,
  fieldError,
  copyStatus,
  copyHelpShown = true,
  onChange,
  onCopy,
}: {
  rule: RuleDraft;
  readOnly: boolean;
  emailsMasked: boolean;
  /** A server message mapped onto this rule's address box. */
  fieldError: string | null;
  /** "Copied to … Save changes to keep it." while it is the last action on this rule. */
  copyStatus: string | null;
  /**
   * Walk 2026-10-02, D14: the copy button's sentence is on screen under the first rule that is on
   * only; under the others it is for a screen reader (each button keeps its description).
   */
  copyHelpShown?: boolean;
  onChange: (next: RuleDraft) => void;
  onCopy: () => void;
}) {
  const id = `${rule.subject}-${rule.outcome}`;
  // The Credentials subject has its own words ("When a credential is refused or expired").
  const label = ruleOutcomeLabel(rule.subject, rule.outcome);
  const policy = useRecipientPolicy();
  const held = useHeldAddresses().get(cellKey(rule.subject, rule.outcome)) ?? [];
  const localProblem = readOnly ? null : emailsProblem(rule.emailsText, policy, held);
  const problem = localProblem ?? fieldError;
  const addresses = emailLines(rule.emailsText);
  const helpId = `copy-rule-help-${id}`;

  return (
    // Round 6 walk W24: the padding is on a wrapper. A fieldset puts its own padding under its
    // legend, so the legend sat on the divider above it.
    <div className="py-3">
      <fieldset className="min-w-0" data-testid={`notification-rule-${id}`}>
        <legend className="mb-2">
          {readOnly ? (
            <SkyText size={300} weight="semibold" testId={`rule-enabled-${id}`}>
              {label}: {rule.enabled ? "On" : "Off"}
            </SkyText>
          ) : (
            <SkySwitch
              label={label}
              checked={rule.enabled}
              testId={`rule-enabled-${id}`}
              onCheckedChange={(checked) => onChange({ ...rule, enabled: checked })}
            />
          )}
        </legend>

        <div>
          {rule.outcome === "success" &&
          rule.subject !== "credentials" &&
          rule.subject !== "data_health" &&
          rule.enabled ? (
            <SkyText as="p" size={200} color="muted" block className="mb-2">
              {SUCCESS_RULE_HELP}
            </SkyText>
          ) : null}

          {rule.outcome === "warning" &&
          (rule.subject === "import" || rule.subject === "schedule") ? (
            <SkyText
              as="p"
              size={200}
              color="muted"
              block
              className="mb-2"
              testId={`rule-warning-help-${id}`}
            >
              {WARNING_RULE_HELP}
            </SkyText>
          ) : null}

          {rule.enabled ? (
            <RuleRecipients
              rule={rule}
              id={id}
              readOnly={readOnly}
              emailsMasked={emailsMasked}
              addresses={addresses}
              problem={problem}
              onChange={onChange}
            />
          ) : null}

          {readOnly || !rule.enabled ? null : (
            <CopyRuleRow
              id={id}
              helpId={helpId}
              copyHelpShown={copyHelpShown}
              copyStatus={copyStatus}
              onCopy={onCopy}
            />
          )}
        </div>
      </fieldset>
    </div>
  );
}
